import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './ManageCompanies.css';

const ManageCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        verificationStatus: verificationFilter !== 'all' ? verificationFilter : undefined
      };
      const response = await api.get('/admin/companies', { params });
      setCompanies(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(error.error?.message || 'Failed to fetch companies');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCompanies();
  };

  const handleVerifyCompany = async () => {
    try {
      await api.post(`/admin/companies/${selectedCompany._id}/verify`, {
        notes: verificationNotes
      });
      toast.success('Company verified successfully');
      setShowVerifyModal(false);
      setVerificationNotes('');
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      console.error('Error verifying company:', error);
      toast.error(error.error?.message || 'Failed to verify company');
    }
  };

  const handleRejectCompany = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.post(`/admin/companies/${selectedCompany._id}/reject`, {
        reason: rejectionReason,
        notes: verificationNotes
      });
      toast.success('Company verification rejected');
      setShowRejectModal(false);
      setRejectionReason('');
      setVerificationNotes('');
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      console.error('Error rejecting company:', error);
      toast.error(error.error?.message || 'Failed to reject company');
    }
  };

  const handleSuspendCompany = async (companyId) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;
    try {
      await api.post(`/admin/companies/${companyId}/suspend`, { reason });
      toast.success('Company suspended successfully');
      fetchCompanies();
    } catch (error) {
      console.error('Error suspending company:', error);
      toast.error(error.error?.message || 'Failed to suspend company');
    }
  };

  const handleActivateCompany = async (companyId) => {
    try {
      await api.post(`/admin/companies/${companyId}/activate`);
      toast.success('Company activated successfully');
      fetchCompanies();
    } catch (error) {
      console.error('Error activating company:', error);
      toast.error(error.error?.message || 'Failed to activate company');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      pending_verification: 'badge-warning',
      suspended: 'badge-danger',
      rejected: 'badge-danger',
      inactive: 'badge-secondary'
    };
    return badges[status] || 'badge-secondary';
  };

  const getVerificationBadge = (verificationStatus) => {
    const badges = {
      verified: 'badge-success',
      pending: 'badge-warning',
      rejected: 'badge-danger',
      needs_documents: 'badge-info'
    };
    return badges[verificationStatus] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="admin-manage-company">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-manage-company">
      <div className="page-header">
        <div>
          <h1>Manage Companies</h1>
          <p>View and manage registered companies</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending_verification">Pending Verification</option>
            <option value="suspended">Suspended</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={verificationFilter}
            onChange={(e) => { setVerificationFilter(e.target.value); }}
            className="filter-select"
          >
            <option value="all">All Verification</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="needs_documents">Needs Documents</option>
          </select>
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>
      </div>

      {/* Companies Grid */}
      <div className={`companies-grid ${companies.length === 0 ? 'empty-100':""}`}>
        {companies.length === 0 ? (
          <div className="empty-state">
            <p>No companies found</p>
            <small>Companies will appear here once employers register</small>
          </div>
        ) : (
          companies.map((company) => (
            <div key={company._id} className="company-card">
              <div className="company-card-header">
                {company.logo ? (
                  <img src={company.logo} alt={company.name} className="company-logo" />
                ) : (
                  <div className="company-logo-placeholder">
                    {company.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="company-info">
                  <h3>{company.name}</h3>
                  <div className="company-badges">
                    <span className={`badge ${getStatusBadge(company.status)}`}>
                      {company.status?.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className={`badge ${getVerificationBadge(company.verificationStatus)}`}>
                      {company.verificationStatus?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="company-card-body">
                <p className="company-description">
                  {company.description || 'No description available'}
                </p>

                <div className="company-details">
                  {company.industry && (
                    <div className="detail-item">
                      <span className="detail-label">Industry:</span>
                      <span className="detail-value">{company.industry}</span>
                    </div>
                  )}
                  {company.size && (
                    <div className="detail-item">
                      <span className="detail-label">Size:</span>
                      <span className="detail-value">{company.size} employees</span>
                    </div>
                  )}
                  {company.location && (
                    <div className="detail-item">
                      <span className="detail-label">Location:</span>
                      <span className="detail-value">
                        {company.location.city}, {company.location.country}
                      </span>
                    </div>
                  )}
                  {company.website && (
                    <div className="detail-item">
                      <span className="detail-label">Website:</span>
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detail-link"
                      >
                        Visit Website →
                      </a>
                    </div>
                  )}
                </div>

                <div className="company-stats">
                  <div className="stat">
                    <span className="stat-value">{company.jobsCount || 0}</span>
                    <span className="stat-label">Jobs Posted</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </span>
                    <span className="stat-label">Joined</span>
                  </div>
                </div>
              </div>

              <div className="company-card-actions">
                {!company.isVerified && company.verificationStatus === 'pending' && (
                  <>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowVerifyModal(true);
                      }}
                    >
                      ✓ Verify
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowRejectModal(true);
                      }}
                    >
                      ✗ Reject
                    </button>
                  </>
                )}
                {company.status === 'suspended' && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleActivateCompany(company._id)}
                  >
                    Activate
                  </button>
                )}
                {company.status === 'active' && company.isVerified && (
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => handleSuspendCompany(company._id)}
                  >
                    Suspend
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="modal-overlay" onClick={() => setShowVerifyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verify Company</h2>
              <button className="modal-close" onClick={() => setShowVerifyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Company:</strong> {selectedCompany?.name}</p>
              <p><strong>Email:</strong> {selectedCompany?.email}</p>
              <p><strong>Website:</strong> {selectedCompany?.website}</p>

              <div className="form-group">
                <label>Verification Notes (Optional)</label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add any notes about the verification..."
                  rows="3"
                  className="form-control"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowVerifyModal(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleVerifyCompany}>
                ✓ Verify Company
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Company Verification</h2>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Company:</strong> {selectedCompany?.name}</p>

              <div className="form-group">
                <label>Rejection Reason <span className="required">*</span></label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                  rows="3"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Additional Notes (Optional)</label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add any additional notes..."
                  rows="2"
                  className="form-control"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleRejectCompany}>
                ✗ Reject Company
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCompanies;
